import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Trash2, Sparkles, RotateCw } from "lucide-react";
import { api } from "@/lib/api";
import { renderMarkdown } from "@/lib/md";

export default function Briefs() {
  const navigate = useNavigate();
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/briefs");
      setBriefs(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const generate = async () => {
    setErr("");
    setGenerating(true);
    try {
      const { data } = await api.post("/briefs/generate");
      setBriefs((b) => [data, ...b]);
    } catch (e) {
      setErr(e?.response?.data?.detail || String(e));
    } finally {
      setGenerating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this brief?")) return;
    await api.delete(`/briefs/${id}`);
    setBriefs((b) => b.filter((x) => x.id !== id));
  };

  return (
    <div className="min-h-screen pb-24 fade-in" data-testid="briefs-page">
      <div className="max-w-[820px] mx-auto px-6 pt-8">
        <button
          onClick={() => navigate("/dashboard")}
          className="text-[13px] text-neutral-600 hover:text-black inline-flex items-center gap-1 mb-6"
        >
          <ChevronLeft size={14} /> Back to dashboard
        </button>

        <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="section-eyebrow flex items-center gap-2">
              <Sparkles size={11} /> Reservoir Brief
            </div>
            <h1 className="font-display text-3xl font-extrabold mt-1 tracking-tight">
              Weekly points strategy
            </h1>
            <p className="text-neutral-600 mt-1 text-[14px]">
              Auto-generated every Monday at 8:00 UTC. Tailored to your balances and expiring credits.
            </p>
          </div>
          <button
            onClick={generate}
            disabled={generating}
            className="btn btn-primary text-[13px]"
            data-testid="briefs-generate-btn"
          >
            <RotateCw size={14} className={generating ? "animate-spin" : ""} />
            {generating ? "Generating…" : "Generate now"}
          </button>
        </div>

        {err && (
          <div
            className="mb-4 p-3 border rounded-[10px] text-[13px]"
            style={{ background: "var(--bad-bg)", color: "var(--bad)", borderColor: "var(--bad-border)" }}
          >
            {err}
          </div>
        )}

        {loading ? (
          <div className="space-y-4">
            <div className="h-32 rounded-[14px] bg-black/[0.03] animate-pulse" />
            <div className="h-32 rounded-[14px] bg-black/[0.03] animate-pulse" />
          </div>
        ) : briefs.length === 0 ? (
          <div className="card p-10 text-center text-[14px] text-neutral-500">
            No briefs yet. Hit "Generate now" to make your first one.
          </div>
        ) : (
          <div className="space-y-4">
            {briefs.map((b) => (
              <article key={b.id} className="card p-6 group" data-testid={`brief-${b.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="text-[12px] text-neutral-500 font-medium">
                      {new Date(b.generated_at).toLocaleString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}{" "}
                      · {b.kind}
                    </div>
                    <h2 className="font-display text-lg font-bold mt-1 tracking-tight">{b.headline}</h2>
                  </div>
                  <button
                    onClick={() => remove(b.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-neutral-400 hover:text-red-700"
                    aria-label="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div
                  className="md text-[14px] leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(b.content) }}
                />
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
